import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { propertyAPI, appointmentAPI, bookingAPI, walletAPI } from '../../lib/api';
import { formatCurrency, formatDate, generateReferralLink } from '../../lib/utils';
import { GlassCard, StatCard, StatusBadge, RankBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Building2,
  Calendar,
  FileText,
  Wallet,
  Users,
  Copy,
  ExternalLink,
  ArrowRight,
  TrendingUp,
  Clock,
} from 'lucide-react';

const CustomerDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    appointments: 0,
    bookings: 0,
    walletBalance: 0,
  });
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [appointmentsRes, bookingsRes, walletRes, propertiesRes] = await Promise.all([
        appointmentAPI.getAppointments(),
        bookingAPI.getBookings(),
        walletAPI.getWallet(),
        propertyAPI.getProperties(),
      ]);

      setStats({
        appointments: appointmentsRes.data.length,
        bookings: bookingsRes.data.length,
        walletBalance: walletRes.data.balance || 0,
      });

      setRecentAppointments(appointmentsRes.data.slice(0, 3));
      setRecentProperties(propertiesRes.data.slice(0, 4));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = generateReferralLink(user?.referral_code);
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Welcome back, {user?.full_name}</h1>
        <p className="text-slate-400 mt-1">Here's what's happening with your property journey</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Appointments"
          value={stats.appointments}
          icon={Calendar}
        />
        <StatCard
          title="Bookings"
          value={stats.bookings}
          icon={FileText}
        />
        <StatCard
          title="Wallet Balance"
          value={formatCurrency(stats.walletBalance)}
          subtitle="Points (MYR 1 = 1 Point)"
          icon={Wallet}
        />
        <GlassCard className="relative overflow-hidden">
          <p className="text-sm text-slate-400 mb-1">KYC Status</p>
          <StatusBadge status={user?.kyc_status} className="mt-2" />
          {user?.kyc_status !== 'approved' && (
            <Link to="/profile">
              <Button size="sm" variant="ghost" className="mt-3 text-emerald-400 hover:text-emerald-300 p-0">
                Complete KYC <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          )}
        </GlassCard>
      </div>

      {/* Referral Card */}
      <GlassCard className="bg-gradient-to-r from-emerald-900/30 to-slate-900/50">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold text-white">Earn with Referrals</h3>
            </div>
            <p className="text-sm text-slate-400">
              Share your referral link and earn commission when your referrals make a purchase
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-4 py-2 bg-slate-800/50 rounded-lg font-mono text-emerald-400">
              {user?.referral_code}
            </div>
            <Button onClick={copyReferralLink} variant="outline" className="border-emerald-500/50 text-emerald-400">
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* Recent Appointments */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Recent Appointments</h2>
          <Link to="/my-appointments">
            <Button variant="ghost" className="text-emerald-400 hover:text-emerald-300">
              View All <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {recentAppointments.length > 0 ? (
            recentAppointments.map((appt) => (
              <GlassCard key={appt.id} hover>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-white">{appt.property?.name}</h3>
                    <p className="text-sm text-slate-400">{appt.property?.location}</p>
                  </div>
                  <StatusBadge status={appt.status} />
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Clock className="w-4 h-4" />
                  {formatDate(appt.preferred_date)} at {appt.preferred_time}
                </div>
              </GlassCard>
            ))
          ) : (
            <GlassCard className="col-span-3 text-center py-8">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No appointments yet</p>
              <Link to="/properties">
                <Button className="mt-4 bg-emerald-500 hover:bg-emerald-600">
                  Browse Properties
                </Button>
              </Link>
            </GlassCard>
          )}
        </div>
      </div>

      {/* Featured Properties */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Featured Properties</h2>
          <Link to="/properties">
            <Button variant="ghost" className="text-emerald-400 hover:text-emerald-300">
              View All <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {recentProperties.map((property) => (
            <Link key={property.id} to={`/properties/${property.id}`}>
              <GlassCard hover className="p-0 overflow-hidden">
                <div
                  className="h-32 bg-cover bg-center"
                  style={{
                    backgroundImage: property.images?.[0]
                      ? `url(${property.images[0]})`
                      : 'url(https://images.unsplash.com/photo-1737823934403-c43c450f25de?crop=entropy&cs=srgb&fm=jpg&q=85)',
                  }}
                />
                <div className="p-4">
                  <h3 className="font-medium text-white truncate">{property.name}</h3>
                  <p className="text-sm text-slate-400 truncate">{property.location}</p>
                  <p className="text-emerald-400 font-mono text-sm mt-2">
                    From {formatCurrency(property.price_range_min)}
                  </p>
                </div>
              </GlassCard>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
